import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

import {
  ASSET,
  ASSET_LABELS,
  ASSET_OPTIONS,
  convert,
  fetchUsdRates,
  formatConverted,
} from "../services/marketRatesApi";
import "./PageStyle.css";

/**
 * Currency converter over live market rates.
 *
 * A public tool: it reads CoinGecko directly and needs no account and no
 * OMJ VISION backend. When a quote cannot be fetched the page says so and the
 * Convert button is unavailable — it never falls back to a stored rate, since
 * a stale or invented number here is indistinguishable from a real one.
 */
function Converter() {
  const [amount, setAmount] = useState("");
  const [from, setFrom] = useState(ASSET.USD);
  const [to, setTo] = useState(ASSET.BTC);
  const [result, setResult] = useState(null);
  const [validationError, setValidationError] = useState(null);

  const [rates, setRates] = useState(null);
  const [ratesError, setRatesError] = useState(null);
  const [isLoadingRates, setIsLoadingRates] = useState(true);

  const loadRates = useCallback(async (signal) => {
    setIsLoadingRates(true);

    try {
      const loaded = await fetchUsdRates({ signal });
      setRates(loaded);
      setRatesError(null);
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      setRates(null);
      setRatesError(error.message || "Market rates are unavailable.");
    } finally {
      setIsLoadingRates(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadRates(controller.signal);

    // Quotes move; an hour-old rate would be quietly wrong.
    const interval = window.setInterval(() => loadRates(), 60000);

    return () => {
      controller.abort();
      window.clearInterval(interval);
    };
  }, [loadRates]);

  // A rate refresh must not leave a stale figure on screen looking current.
  useEffect(() => {
    setResult(null);
  }, [from, to, amount]);

  const handleConvert = () => {
    setValidationError(null);
    setResult(null);

    const parsed = Number(amount);

    if (!amount.trim() || !Number.isFinite(parsed)) {
      setValidationError("Enter a valid number.");
      return;
    }

    if (parsed < 0) {
      setValidationError("Enter a positive amount.");
      return;
    }

    if (!rates) {
      setValidationError("Market rates are not available right now.");
      return;
    }

    const converted = convert(parsed, from, to, rates.usdPrices);

    if (converted === null) {
      setValidationError(
        `No live rate is available for ${ASSET_LABELS[from]} → ${ASSET_LABELS[to]}.`
      );
      return;
    }

    setResult(
      `${parsed.toLocaleString()} ${ASSET_LABELS[from]} = ${formatConverted(converted)} ${ASSET_LABELS[to]}`
    );
  };

  const canConvert = Boolean(rates) && !isLoadingRates;

  return (
    <div className="page-container">
      <h1>💱 Currency Converter</h1>
      <p>Convert between USD, Bitcoin (BTC), Ethereum (ETH), and Gold (XAU) at live market rates.</p>

      <div className="converter-box">
        <input
          type="number"
          placeholder="Enter amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />

        <div className="select-row">
          <select
            value={from}
            aria-label="Convert from"
            onChange={(e) => setFrom(e.target.value)}
          >
            {ASSET_OPTIONS.map((asset) => (
              <option key={asset} value={asset}>
                {ASSET_LABELS[asset]}
              </option>
            ))}
          </select>

          <span className="arrow">➡️</span>

          <select
            value={to}
            aria-label="Convert to"
            onChange={(e) => setTo(e.target.value)}
          >
            {ASSET_OPTIONS.map((asset) => (
              <option key={asset} value={asset}>
                {ASSET_LABELS[asset]}
              </option>
            ))}
          </select>
        </div>

        <button onClick={handleConvert} disabled={!canConvert}>
          Convert
        </button>

        {result && <p className="result">{result}</p>}
        {validationError && <p className="converter-error">{validationError}</p>}

        <p className="converter-rates">
          {isLoadingRates && !rates
            ? "Loading live rates…"
            : ratesError
              ? `⚠️ ${ratesError} Conversion is unavailable until rates load.`
              : rates
                ? `Live rates · updated ${rates.fetchedAt.toLocaleTimeString()}`
                : null}
        </p>
      </div>
    </div>
  );
}

export default Converter;
